<script setup lang="ts">
import { KsButton } from "@aschehoug/kloss";

defineProps<{ content: CTAContent[] }>();

function handleUrl(url: string) {
  if (url.startsWith("http")) {
    window.open(url, "_blank");
  } else {
    navigateTo(url);
  }
}

const currentArticle = inject(keys.currentArticle);

interface ThemeAssociation {
  themes: string[];
  backgroundClass: string;
  images: string[];
}

const themeBackgroundAssociation: Array<ThemeAssociation> = [
  {
    themes: ["dark-green-pink", "dark-red-pink", "blue-pink"],
    backgroundClass: "bg-coral-10",
    images: ["rosa_ill1.svg", "rosa_ill2.svg"],
  },
  {
    themes: ["green-seagreen", "seagreen-light-blue"],
    backgroundClass: "bg-green-10",
    images: ["gronn_ill1.svg", "gronn_ill2.svg"],
  },
  {
    themes: ["green-yellow-light", "gray-yellow"],
    backgroundClass: "bg-yellow-20",
    images: ["gul_ill1.svg", "gul_ill2.svg"],
  },
];

const bg = computed(() => {
  const theme = currentArticle?.colorTheme;
  if (theme) {
    const association = themeBackgroundAssociation.find((a) =>
      a.themes.includes(theme),
    );
    if (association) {
      return {
        backgroundClass: association.backgroundClass,
        images: association.images,
      };
    }
  }
  return {
    backgroundClass: "bg-coral-10",
    images: ["rosa_ill1.svg", "rosa_ill2.svg"],
  };
});

const linkConfig = useCtaLinks();
</script>

<template>
  <div
    v-for="(ctaContent, index) in content"
    :key="index"
    class="cta"
    :class="bg.backgroundClass"
  >
    <StaticAsset
      v-if="bg.images[index]"
      folder="graphics"
      :file="bg.images[index]"
      aria-hidden="true"
    />
    <h3>
      {{ ctaContent.title }}
    </h3>
    <div v-html="ctaContent.content" />
    <KsButton
      v-if="ctaContent.link"
      class="action__button"
      variant="primary"
      shape="rounded"
      size="large"
      @click="handleUrl(ctaContent.link)"
    >
      {{ ctaContent.label }}
    </KsButton>
    <p v-if="ctaContent.links.length > 0">
      Har du ikke tilgang?
    </p>
    <NuxtLink
      v-for="link in ctaContent.links"
      :key="link"
      :to="linkConfig[link].to"
    >
      {{ linkConfig[link].label }}
    </NuxtLink>
  </div>
</template>

<style scoped lang="scss">
.cta {
  position: relative;
  display: flex;
  flex-flow: column;
  gap: pixelsToRem(18);
  align-items: center;
  width: 100%;
  max-width: pixelsToRem(470);
  padding: 2rem 2rem 4rem 2rem;
  border-radius: theme("borderRadius.xl");
  border: 0.1rem solid white;
  color: theme("colors.green.50");
  height: auto;

  img {
    margin: 0;
  }

  h3,
  p,
  a,
  div > p {
    text-align: center;
    color: theme("colors.green.50");
  }

  h3 {
    font-size: pixelsToRem(20);
    line-height: pixelsToRem(32);
    font-weight: 600;
  }

  div {
    padding-bottom: 1.25rem;
  }

  p {
    font-size: pixelsToRem(18);
    line-height: pixelsToRem(28);
    font-weight: 400;
    padding-top: 1.25rem;
  }

  a {
    font-weight: 600;
  }
}
</style>
